import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import Select from "react-select";
import * as XLSX from "xlsx";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Layout from "../../Component/Layout";
import { MASTER_API, ORDER_API } from "../../apiConfig";

const API_BASE = MASTER_API;

/* ===============================
   SHARED STYLES (matches Product.js)
================================ */

const customSelectStyles = {
  control: (base) => ({
    ...base,
    minHeight: 32,
    height: 32,
    fontSize: "13px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" }
  }),
  valueContainer: (base) => ({
    ...base,
    padding: "0 8px",
    height: "32px",
    display: "flex",
    alignItems: "center"
  }),
  input: (base) => ({ ...base, margin: 0, padding: 0 }),
  dropdownIndicator: (base) => ({ ...base, padding: 4 }),
  menu: (base) => ({ ...base, fontSize: "13px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 9999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
      ? "#f1f5f9"
      : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "13px"
  })
};

const compactSelectStylesSmall = {
  control: (base) => ({
    ...base,
    minHeight: 30,
    fontSize: "12px",
    borderColor: "#dee2e6",
    boxShadow: "none",
    "&:hover": { borderColor: "#b3b3b3" }
  }),
  valueContainer: (base) => ({ ...base, padding: "2px 6px", flexWrap: "wrap" }),
  input: (base) => ({ ...base, margin: 0, padding: 0, fontSize: "12px" }),
  indicatorsContainer: (base) => ({ ...base, alignSelf: "flex-start", paddingTop: 2 }),
  dropdownIndicator: (base) => ({ ...base, padding: 2 }),
  menu: (base) => ({ ...base, fontSize: "12px", zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 99999 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#1e293b"
      : state.isFocused
      ? "#f1f5f9"
      : "white",
    color: state.isSelected ? "white" : "#334155",
    cursor: "pointer",
    fontSize: "12px"
  })
};

// Top filter bar fields, each mapped to its dropdown-data endpoint
// (mirrors Pegging.js / Product Master's Product Type-driven filter bar).
const FILTER_FIELDS = [
  { label: "Product Type", key: "productType", dataKey: "productTypes" },
  { label: "Category", key: "category", dataKey: "categories" },
  { label: "Cable Type", key: "cableType", dataKey: "cableTypes" },
  { label: "Code", key: "code", dataKey: "codes" },
  { label: "Color", key: "color", dataKey: "colours" },
  { label: "Cores", key: "noOfCores", dataKey: "cores" },
  { label: "Cross Section", key: "nominalCrossSection", dataKey: "nominalCrossSections" },
  { label: "Std. Ref", key: "referenceOfStandard", dataKey: "referenceOfStandards" },
  { label: "Voltage", key: "voltage", dataKey: "voltages" },
  { label: "Construction", key: "construction", dataKey: "constructions" },
  { label: "Sub Category", key: "subCategory", dataKey: "subCategories" },
  { label: "Quality Category", key: "qualityCategory", dataKey: "qualityCategories" }
];

// Only an exact "Raw Material" match is treated as Raw Material; every other
// product type (Finished Goods, Semi Finished, Packing, ...) gets the cable-spec set.
const isRawMaterialType = (productType) =>
  (productType || "").trim().toLowerCase() === "raw material";

const CABLE_SPEC_FILTER_KEYS = [
  "category",
  "cableType",
  "code",
  "color",
  "noOfCores",
  "nominalCrossSection",
  "referenceOfStandard",
  "voltage",
  "construction"
];
const RAW_MATERIAL_FILTER_KEYS = ["category", "subCategory", "qualityCategory"];

// Filter keys whose options depend on Product Type and get cleared when it changes.
const DEPENDENT_FILTER_KEYS = [
  "category",
  "cableType",
  "code",
  "color",
  "noOfCores",
  "nominalCrossSection",
  "referenceOfStandard",
  "voltage",
  "construction",
  "subCategory",
  "qualityCategory"
];

const compactFilterBox = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 8,
  padding: "10px 12px",
  marginBottom: 14
};

const compactFilterItemLabel = {
  fontSize: 11,
  fontWeight: 600,
  color: "#64748b",
  whiteSpace: "nowrap",
  letterSpacing: "0.02em",
  paddingLeft: 2
};

const styles = {
  pageTitleBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 16
  },
  pageTitle: { fontSize: 20, fontWeight: 700, color: "#1e293b", margin: 0 },
  pageSubtitle: { fontSize: 12, color: "#64748b", margin: 0 },
  filterCard: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 15,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    marginBottom: 15
  },
  compactLabel: {
    fontSize: "12px",
    fontWeight: 600,
    marginBottom: "4px",
    color: "#334155",
    display: "block"
  },
  compactInput: {
    fontSize: "13px",
    height: "32px",
    padding: "4px 8px",
    width: "100%",
    borderRadius: 6,
    border: "1px solid #dee2e6",
    boxSizing: "border-box",
    outline: "none"
  },
  compactInputDisabled: {
    fontSize: "13px",
    height: "32px",
    padding: "4px 8px",
    width: "100%",
    borderRadius: 6,
    border: "1px solid #dee2e6",
    boxSizing: "border-box",
    background: "#f1f5f9",
    color: "#64748b"
  },
  fieldBox: { minWidth: 160, flex: "1 1 160px" },
  primaryBtn: {
    height: "38px",
    padding: "0 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: "6px",
    whiteSpace: "nowrap",
    background: "#021843",
    color: "#fff",
    border: "1px solid #021843",
    outline: "none",
    boxShadow: "none",
    cursor: "pointer"
  },
  secondaryBtn: {
    height: "38px",
    padding: "0 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: "6px",
    whiteSpace: "nowrap",
    background: "#ffffff",
    color: "#021843",
    border: "1px solid #021843",
    outline: "none",
    boxShadow: "none",
    cursor: "pointer"
  },
  secondaryBtnDisabled: {
    height: "38px",
    padding: "0 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    fontWeight: 600,
    fontSize: 13,
    borderRadius: "6px",
    whiteSpace: "nowrap",
    background: "#f1f5f9",
    color: "#94a3b8",
    border: "1px solid #e2e8f0",
    cursor: "not-allowed"
  },
  tableContainer: {
    background: "#ffffff",
    borderRadius: 8,
    padding: 10,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)"
  },
  erpTable: { width: "100%", borderCollapse: "collapse" },
  erpTh: {
    background: "#f8fafc",
    fontSize: 12,
    fontWeight: 600,
    padding: "10px",
    borderBottom: "1px solid #e5e7eb",
    color: "#1e293b",
    textAlign: "left"
  },
  erpTd: {
    fontSize: 13,
    padding: "10px",
    borderBottom: "1px solid #f1f5f9",
    color: "#334155"
  },
  badge: {
    padding: "4px 8px",
    borderRadius: 4,
    fontWeight: 600,
    fontSize: 12,
    display: "inline-block"
  },
  banner: {
    marginTop: 15,
    marginBottom: 15,
    padding: "10px 14px",
    background: "#ffebee",
    color: "#c62828",
    fontWeight: 600,
    borderRadius: 6,
    fontSize: 13
  },
  modalBackdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(0,0,0,0.45)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1050
  },
  modal: {
    background: "#fff",
    borderRadius: 8,
    width: "80vw",
    height: "85vh",
    display: "flex",
    flexDirection: "column",
    boxShadow: "0 20px 40px rgba(0,0,0,0.25)"
  },
  modalHeader: {
    padding: "14px 20px",
    borderBottom: "1px solid #e5e7eb",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  modalBody: { padding: "16px 20px", overflowY: "auto", flex: 1 }
};

const Lead = () => {
  const [productCodes, setProductCodes] = useState([]);
  const [selectedProductCode, setSelectedProductCode] = useState(null);
  const [tableData, setTableData] = useState([]);
  const [simulatedData, setSimulatedData] = useState([]);
  const [headingMessage, setHeadingMessage] = useState("");
  const [showModal, setShowModal] = useState(false);
  // When set, the BOM Schedule modal shows THIS accordion group's tree
  // (multi-finished-good mode) instead of the single-product scheduleTree.
  const [modalGroup, setModalGroup] = useState(null);
  const [scheduleTree, setScheduleTree] = useState(null);
  // Tree shown in the BOM Schedule modal: the clicked accordion's, else the single-product one.
  const activeScheduleTree = modalGroup ? modalGroup.scheduleTree : scheduleTree;
  const [expandedNodes, setExpandedNodes] = useState({});

  // --- Spec filter bar (Product Type / Category / Cable Type / ... ), matching Pegging.js ---
  const [dropdownData, setDropdownData] = useState({
    productTypes: [],
    categories: [],
    cableTypes: [],
    codes: [],
    colours: [],
    cores: [],
    nominalCrossSections: [],
    referenceOfStandards: [],
    voltages: [],
    constructions: [],
    subCategories: [],
    qualityCategories: []
  });
  const [filterValues, setFilterValues] = useState({});
  const [filtersOpen, setFiltersOpen] = useState(true);

  // Sales Order / Item No / Planned Order / Purchase Request / Production Order /
  // Purchase Order / Stock row, matching Pegging.js's per-column filter row.
  const [categoryOptions, setCategoryOptions] = useState({
    "Sales Order": [],
    "Planned Order": [],
    "Purchase Request": [],
    "Production Order": [],
    "Purchase Order": [],
    Stock: []
  });
  const [categoryElementFilters, setCategoryElementFilters] = useState({});
  const [soItemNoFilter, setSoItemNoFilter] = useState("");
  // Every Sales Order from the sales_order table (independent of pegging rows).
  const [allSalesOrders, setAllSalesOrders] = useState([]);
  useEffect(() => {
    axios
      .get(`${API_BASE}/leadtime/sales-orders`)
      .then((res) => setAllSalesOrders(res.data || []))
      .catch((err) => {
        console.error("Failed to load sales orders:", err.response?.data || err.message);
      });
  }, []);
  const [salesOrderItemNoOptions, setSalesOrderItemNoOptions] = useState([]);

  const TOP_CATEGORY_FILTERS = [
    "Sales Order",
    "Planned Order",
    "Purchase Request",
    "Production Order",
    "Purchase Order",
    "Stock"
  ];

  const handleCategoryElementFilterChange = (cat, value) => {
    setCategoryElementFilters((prev) => {
      const next = { ...prev };
      if (value) next[cat] = value;
      else delete next[cat];
      return next;
    });
    if (cat === "Sales Order") setSoItemNoFilter("");
  };

  const [factor, setFactor] = useState(1);
  const [length, setLength] = useState(1);
  const [requirementDate, setRequirementDate] = useState("");
  const requirementDateRef = useRef(null);

  // Multi-finished-good Simulate results — populated instead of simulatedData/
  // scheduleTree when the selected document (Sales Order with no single Item No
  // narrowed down, or any of Planned/Purchase Request/Production/Purchase
  // Order/Stock pegged to several SO lines) resolves to MORE than one
  // (Sales Order, Item No) pair. One accordion entry per pair/finished good.
  const [multiSimResults, setMultiSimResults] = useState([]);
  const [multiSimExpanded, setMultiSimExpanded] = useState({});
  const [isSimulating, setIsSimulating] = useState(false);

  // Same param shape fetchCategoryFilterOptions sends to /pegging/filter-options —
  // shared so /leadtime/sales-order-pairs is always asked about the exact same
  // selection at Simulate time.
  const buildCategoryFilterParams = useCallback(
    () => ({
      productType: filterValues.productType || "",
      category: filterValues.category || "",
      cableType: filterValues.cableType || "",
      code: filterValues.code || "",
      color: filterValues.color || "",
      noOfCores: filterValues.noOfCores || "",
      nominalCrossSection: filterValues.nominalCrossSection || "",
      referenceOfStandard: filterValues.referenceOfStandard || "",
      voltage: filterValues.voltage || "",
      construction: filterValues.construction || "",
      subCategory: filterValues.subCategory || "",
      qualityCategory: filterValues.qualityCategory || "",
      salesOrder: categoryElementFilters["Sales Order"] || "",
      itemNo: soItemNoFilter || "",
      plannedOrder: categoryElementFilters["Planned Order"] || "",
      purchaseRequest: categoryElementFilters["Purchase Request"] || "",
      productionOrder: categoryElementFilters["Production Order"] || "",
      purchaseOrder: categoryElementFilters["Purchase Order"] || "",
      stock: categoryElementFilters["Stock"] || ""
    }),
    [filterValues, categoryElementFilters, soItemNoFilter]
  );

  const calculatedQty = Number(factor) * Number(length);

  // dd/mm/yyyy formatter used everywhere a date is shown in the UI.
  const formatDate = (date) => {
    const d = date instanceof Date ? date : new Date(date);
    if (isNaN(d.getTime())) return "-";
    const dd = String(d.getDate()).padStart(2, "0");
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const yyyy = d.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  useEffect(() => {
    fetchProductCodes();
    fetchAllProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchProductCodes = async () => {
    try {
      const res = await axios.get(`${API_BASE}/leadtime/product-codes`);
      setProductCodes(res.data || []);
    } catch (err) {
      console.error("fetchProductCodes failed:", err.response?.data || err.message);
      toast.error("Failed to load product codes");
    }
  };

  const fetchAllProducts = async () => {
    try {
      const res = await axios.get(`${API_BASE}/leadtime/all`);
      formatAndSetTable(res.data);
    } catch (err) {
      console.error("fetchAllProducts failed:", err.response?.data || err.message);
      toast.error("Failed to load products");
    }
  };

  const formatAndSetTable = (data) => {
    const formatted = (data || []).map((row) => ({
      productCode: row.productCode,
      procurementType: row.procurementType || "",
      productionLeadTime: row.productionLeadTime || 0,
      procurementLeadTime: row.procurementLeadTime || 0,
      baseUnit: row.baseUnit || 1,
      routingSetup: row.routingSetup || 0,
      routingMachine: row.routingMachine || 0,
      routingLabour: row.routingLabour || 0,
      ownTime: "-",
      calculatedLeadTime: "-"
    }));

    setTableData(formatted);
    setSimulatedData([]);
  };

  const validateRequirementDate = (leadTime, reqDateOverride) => {
    const reqDateStr = reqDateOverride !== undefined ? reqDateOverride : requirementDate;
    if (!reqDateStr || !leadTime || leadTime === "-") {
      return null;
    }

    const today = new Date();
    const reqDate = new Date(reqDateStr);
    const lt = Number(leadTime);

    const startDate = new Date(reqDate);
    startDate.setDate(startDate.getDate() - lt);

    if (startDate >= today) {
      return {
        status: "valid",
        message: `Requirement date ${formatDate(reqDate)} is possible`
      };
    } else {
      const newReq = new Date(today);
      newReq.setDate(today.getDate() + lt);

      return {
        status: "invalid",
        message: `Requirement date changed to ${formatDate(newReq)}`
      };
    }
  };

  const productCodeOptions = productCodes.map((p) => ({
    value: p.productCode,
    label: p.description ? `${p.productCode} - ${p.description}` : p.productCode
  }));

  // Updates a top filter bar field. Changing Product Type also clears every
  // dependent filter, since their available options change with it.
  const handleFilterChange = (key, selected) => {
    setFilterValues((prev) => {
      const next = { ...prev };
      if (selected?.value) next[key] = selected.value;
      else delete next[key];
      if (key === "productType") {
        DEPENDENT_FILTER_KEYS.forEach((k) => delete next[k]);
      }
      return next;
    });
  };

  const clearAllFilters = () => {
    setFilterValues({});
    setCategoryElementFilters({});
    setSoItemNoFilter("");
  };

  const activeFilterCount =
    Object.keys(filterValues).length +
    Object.keys(categoryElementFilters).length +
    (soItemNoFilter ? 1 : 0);

  // Loads the Product Type dropdown once on mount — the only top filter bar
  // field whose options never depend on another filter.
  useEffect(() => {
    const fetchProductTypes = async () => {
      try {
        const res = await axios.get(`${API_BASE}/configuration/product-types`);
        setDropdownData((prev) => ({ ...prev, productTypes: res.data || [] }));
      } catch (err) {
        console.error("Failed to load product types:", err);
      }
    };
    fetchProductTypes();
  }, []);

  // Loads Category/Cable Type/Code/Color/Cores/Cross Section/Std. Ref/Voltage/Construction
  // options scoped to whatever's currently selected, cascading the same way Pegging/Product
  // Master do — picking a Product Type narrows these dropdowns to only the values that
  // actually occur for that product type, and each further selection narrows the rest.
  const fetchDependentFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${API_BASE}/product-master/filter-options`, {
        params: {
          productType: filterValues.productType || "",
          category: filterValues.category || "",
          cableType: filterValues.cableType || "",
          code: filterValues.code || "",
          color: filterValues.color || "",
          noOfCores: filterValues.noOfCores || "",
          nominalCrossSection: filterValues.nominalCrossSection || "",
          referenceOfStandard: filterValues.referenceOfStandard || "",
          voltage: filterValues.voltage || "",
          construction: filterValues.construction || "",
          subCategory: filterValues.subCategory || "",
          qualityCategory: filterValues.qualityCategory || ""
        }
      });
      const data = res.data || {};
      const toNameObjs = (arr) => (arr || []).map((v) => ({ name: v }));
      setDropdownData((prev) => ({
        ...prev,
        categories: toNameObjs(data.category),
        cableTypes: toNameObjs(data.cableType),
        codes: toNameObjs(data.code),
        colours: toNameObjs(data.color),
        cores: toNameObjs(data.noOfCores),
        nominalCrossSections: toNameObjs(data.nominalCrossSection),
        referenceOfStandards: toNameObjs(data.referenceOfStandard),
        voltages: toNameObjs(data.voltage),
        constructions: toNameObjs(data.construction),
        subCategories: toNameObjs(data.subCategory),
        qualityCategories: toNameObjs(data.qualityCategory)
      }));
    } catch (err) {
      console.error("Failed to load dependent filter options:", err);
    }
  }, [
    filterValues.productType,
    filterValues.category,
    filterValues.cableType,
    filterValues.code,
    filterValues.color,
    filterValues.noOfCores,
    filterValues.nominalCrossSection,
    filterValues.referenceOfStandard,
    filterValues.voltage,
    filterValues.construction,
    filterValues.subCategory,
    filterValues.qualityCategory
  ]);

  useEffect(() => {
    fetchDependentFilterOptions();
  }, [fetchDependentFilterOptions]);

  // Loads the Sales Order/Item No/Planned Order/Purchase Request/Production Order/
  // Purchase Order/Stock dropdown options — same /pegging/filter-options endpoint
  // Pegging.js uses, scoped by whatever spec filters + per-column values are active.
  const fetchCategoryFilterOptions = useCallback(async () => {
    try {
      const res = await axios.get(`${ORDER_API}/pegging/filter-options`, {
        params: buildCategoryFilterParams()
      });
      const data = res.data || {};
      setCategoryOptions({
        "Sales Order": data.salesOrder || [],
        "Planned Order": data.plannedOrder || [],
        "Purchase Request": data.purchaseRequest || [],
        "Production Order": data.productionOrder || [],
        "Purchase Order": data.purchaseOrder || [],
        Stock: data.stock || []
      });
      // NOTE: Item No options are populated separately, from
      // /leadtime/sales-order-lines (see the effect below) — NOT from
      // data.itemNo here. Pegging's own itemNo is inconsistent (sometimes
      // the clean ordinal, sometimes the raw sales_order.id for the same
      // line), which is what caused duplicate-looking entries like
      // "1, 2, 378, 379" for what is really just 2 lines.
    } catch (err) {
      console.error("Failed to load Sales Order/Stock filter options:", err);
    }
  }, [buildCategoryFilterParams]);

  // Resolves a Sales Order + Item No (ordinal OR raw id) into Product Code /
  // Requirement Date / Qty / No. of Drum/Coil / Length of Drum/Coil via
  // GET /leadtime/simulate-inputs, and autofills the Simulate form.
  // Returns the resolved values (or null on failure) so callers such as
  // handleSimulate can use them immediately without waiting for state updates.
  const fetchSimulateInputs = useCallback(
    async (salesOrder, itemNo, { silent = false } = {}) => {
      try {
        const res = await axios.get(`${API_BASE}/leadtime/simulate-inputs`, {
          params: { salesOrder, itemNo }
        });
        const data = res.data || {};

        if (data.productCode) {
          const matchedOption =
            productCodeOptions.find((opt) => opt.value === data.productCode) || {
              value: data.productCode,
              label: data.productCode
            };
          setSelectedProductCode(matchedOption);
        }

        let reqDateStr = "";
        if (data.requirementDate) {
          const d = new Date(data.requirementDate);
          if (!isNaN(d.getTime())) {
            reqDateStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
              d.getDate()
            ).padStart(2, "0")}`;
            setRequirementDate(reqDateStr);
          }
        }

        if (data.noOfDrumCoil !== null && data.noOfDrumCoil !== undefined) {
          setFactor(data.noOfDrumCoil);
        }
        if (data.lengthOfDrumCoil !== null && data.lengthOfDrumCoil !== undefined) {
          setLength(data.lengthOfDrumCoil);
        }

        if (!silent) toast.success("Simulate fields auto-filled from Sales Order");
        return { ...data, requirementDateStr: reqDateStr };
      } catch (err) {
        console.error("fetchSimulateInputs failed:", err.response?.data || err.message);
        toast.error(
          err.response?.data?.message || "Could not auto-fill Simulate fields for that Sales Order/Item No"
        );
        return null;
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productCodeOptions]
  );

  // Works out WHICH (Sales Order, Item No) finished goods the current selection
  // refers to. Item No is always the ordinal (1, 2, 3...).
  //   • Planned/PR/Production/PO/Stock selected -> the pairs pegged to that
  //     document (1 or many Sales Orders / lines)
  //   • Sales Order + Item No                   -> that single line
  //   • Sales Order only                        -> ALL lines of that Sales Order
  const resolveSimulatePairs = async () => {
    const so = categoryElementFilters["Sales Order"] || "";
    const hasDocument = [
      "Planned Order",
      "Purchase Request",
      "Production Order",
      "Purchase Order",
      "Stock"
    ].some((k) => categoryElementFilters[k]);

    if (hasDocument) {
      const res = await axios.get(`${API_BASE}/leadtime/sales-order-pairs`, {
        params: buildCategoryFilterParams()
      });
      let pairs = res.data || [];
      if (so) {
        pairs = pairs.filter(
          (p) => String(p.salesOrder).trim().toLowerCase() === so.trim().toLowerCase()
        );
      }
      if (soItemNoFilter) {
        pairs = pairs.filter((p) => String(p.itemNo) === String(soItemNoFilter));
      }
      return pairs;
    }

    if (so && soItemNoFilter) {
      return [{ salesOrder: so, itemNo: String(soItemNoFilter) }];
    }

    if (so) {
      const res = await axios.get(`${API_BASE}/leadtime/sales-order-lines`, {
        params: { salesOrder: so }
      });
      return (res.data || []).map((l) => ({ salesOrder: so, itemNo: String(l.itemNo) }));
    }

    return [];
  };

  // Autofill Product Code / Qty / No. of Drum/Coil / Length / Requirement Date
  // whenever the selection resolves to exactly ONE finished good. With several,
  // the form is left alone and Simulate runs every one of them.
  useEffect(() => {
    const hasSelection = TOP_CATEGORY_FILTERS.some((k) => categoryElementFilters[k]);
    if (!hasSelection) return;
    let cancelled = false;
    (async () => {
      try {
        const pairs = await resolveSimulatePairs();
        if (cancelled) return;
        if (pairs.length === 1) {
          await fetchSimulateInputs(pairs[0].salesOrder, pairs[0].itemNo);
        } else if (pairs.length > 1) {
          toast.info(`${pairs.length} finished goods found — Simulate will run all of them`);
        }
      } catch (err) {
        console.error("Auto-fill pair resolution failed:", err.response?.data || err.message);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryElementFilters, soItemNoFilter]);

  useEffect(() => {
    fetchCategoryFilterOptions();
  }, [fetchCategoryFilterOptions]);

  // Loads the Item No dropdown options for whichever Sales Order is
  // currently selected, from the clean ordinal-only /leadtime/sales-order-lines
  // endpoint (1, 2, 3...) — NOT from /pegging/filter-options, whose itemNo
  // values can be either the ordinal or the raw sales_order.id for the same
  // line, depending on which join path built that pegging row.
  useEffect(() => {
    const selectedSalesOrder = categoryElementFilters["Sales Order"];
    if (!selectedSalesOrder) {
      setSalesOrderItemNoOptions([]);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API_BASE}/leadtime/sales-order-lines`, {
        params: { salesOrder: selectedSalesOrder }
      })
      .then((res) => {
        if (cancelled) return;
        setSalesOrderItemNoOptions((res.data || []).map((line) => line.itemNo));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("Failed to load sales-order-lines:", err.response?.data || err.message);
        setSalesOrderItemNoOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, [categoryElementFilters]);

  // Runs the existing /leadtime/simulate + /leadtime/schedule pair for one
  // productCode/qty/requirementDate and returns the row set + schedule tree,
  // without touching any component state. Shared by the single-product
  // handleSimulate flow and the multi-finished-good accordion flow.
  const runSimulationFor = async (productCode, qty, reqDate) => {
    const simRes = await axios.get(`${API_BASE}/leadtime/simulate`, {
      params: { qty, productCode }
    });

    const updated = simRes.data.map((matched) => {
      const master = tableData.find((t) => t.productCode === matched.productCode);
      return {
        productCode: matched.productCode,
        description: matched.description || master?.description || "",
        procurementType: matched.procurementType,
        productType: matched.productType || "",
        qty: matched.qty,
        uom: matched.uom || "",
        productionLeadTime: matched.productionLeadTime,
        procurementLeadTime: matched.procurementLeadTime,
        baseUnit: master?.baseUnit || 1,
        ownTime: matched.ownTime,
        calculatedLeadTime: matched.totalLeadTime,
        setupTime: matched.setupTime,
        machineTime: matched.machineTime,
        labourTime: matched.labourTime,
        startDate: "-",
        endDate: "-"
      };
    });

    const scheduleRes = await axios.get(`${API_BASE}/leadtime/schedule`, {
      params: { productCode: productCode || null, requirementDate: reqDate, qty }
    });

    let dateMap = {};
    const flatten = (node) => {
      if (!node) return;
      const startDt = new Date(node.startDate);
      const endDt = new Date(node.endDate);
      dateMap[node.productCode] = {
        startTs: startDt.getTime(),
        endTs: endDt.getTime(),
        startDate: formatDate(startDt),
        endDate: formatDate(endDt),
        startTime: startDt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
        endTime: endDt.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      };
      if (node.children && node.children.length > 0) node.children.forEach(flatten);
    };
    if (scheduleRes.data.schedule) flatten(scheduleRes.data.schedule);

    const updatedWithDates = updated.map((row) => ({
      ...row,
      startTs: dateMap[row.productCode]?.startTs ?? null,
      endTs: dateMap[row.productCode]?.endTs ?? null,
      startDate: dateMap[row.productCode]?.startDate || "-",
      endDate: dateMap[row.productCode]?.endDate || "-",
      startTime: dateMap[row.productCode]?.startTime || "-",
      endTime: dateMap[row.productCode]?.endTime || "-"
    }));

    return {
      rows: updatedWithDates,
      scheduleTree: scheduleRes.data.schedule || null,
      headingMessage: scheduleRes.data.headingMessage || ""
    };
  };

  const handleSimulate = async () => {
    setIsSimulating(true);
    try {
      // Check whether the currently-selected document (Sales Order with no
      // single Item No narrowed down, or any of Planned/Purchase Request/
      // Production/Purchase Order/Stock) is pegged to more than one
      // (Sales Order, Item No) — i.e. more than one finished good.
      let pairs = [];
      try {
        pairs = await resolveSimulatePairs();
      } catch (err) {
        console.error("Resolving Sales Order lines failed:", err.response?.data || err.message);
      }

      if (pairs.length > 1) {
        // ── MULTI-FINISHED-GOOD MODE ──────────────────────────────────────
        setSimulatedData([]);
        setScheduleTree(null);
        setHeadingMessage("");

        const groups = [];
        const failures = [];
        for (const pair of pairs) {
          try {
            const inputsRes = await axios.get(`${API_BASE}/leadtime/simulate-inputs`, {
              params: { salesOrder: pair.salesOrder, itemNo: pair.itemNo }
            });
            const inputs = inputsRes.data || {};
            if (!inputs.productCode) {
              const reason = `no product code resolved for Item No ${pair.itemNo} (check /leadtime/simulate-inputs?salesOrder=${pair.salesOrder}&itemNo=${pair.itemNo})`;
              console.warn(`Skipping SO ${pair.salesOrder} / Item ${pair.itemNo}: ${reason}`);
              failures.push({ pair, reason });
              continue;
            }

            let reqDate = requirementDate;
            if (inputs.requirementDate) {
              const d = new Date(inputs.requirementDate);
              if (!isNaN(d.getTime())) {
                reqDate = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
                  d.getDate()
                ).padStart(2, "0")}`;
              }
            }
            if (!reqDate) {
              const reason = "no requirement date (sales_order.requirement_date is empty and the form's Requirement Date field is also empty)";
              console.warn(`Skipping SO ${pair.salesOrder} / Item ${pair.itemNo}: ${reason}`);
              failures.push({ pair, reason });
              continue;
            }
            const drumFactor = Number(inputs.noOfDrumCoil) || 1;
            const drumLength = Number(inputs.lengthOfDrumCoil) || 1;
            const qty = drumFactor * drumLength || Number(inputs.qty) || 1;

            const { rows, scheduleTree, headingMessage } = await runSimulationFor(
              inputs.productCode,
              qty,
              reqDate
            );

            groups.push({
              key: `${pair.salesOrder}::${pair.itemNo}`,
              salesOrder: pair.salesOrder,
              itemNo: pair.itemNo,
              productCode: inputs.productCode,
              requirementDate: reqDate,
              qty,
              rows,
              scheduleTree,
              headingMessage
            });
          } catch (err) {
            const reason = err.response?.data?.message || err.message || "unknown error";
            console.error(`Simulate failed for SO ${pair.salesOrder} / Item ${pair.itemNo}:`, err.response?.data || err);
            failures.push({ pair, reason });
          }
        }

        setMultiSimResults(groups);
        setMultiSimExpanded(
          groups.reduce((acc, g, i) => ({ ...acc, [g.key]: i === 0 }), {})
        );

        if (groups.length) {
          toast.success(`Simulation completed for ${groups.length} finished good(s)`);
          if (failures.length) {
            console.warn("Some finished goods were skipped:", failures);
          }
        } else {
          console.error("Simulation failed for every finished good — details:", failures);
          const firstReason = failures[0]?.reason;
          toast.error(
            firstReason
              ? `Simulation failed for every finished good: ${firstReason}`
              : "Simulation failed for every finished good on this document"
          );
        }
        return;
      }

      // ── SINGLE FINISHED GOOD FROM A SALES ORDER / DOCUMENT ───────────────
      // Exactly one (Sales Order, Item No): fill the form and run it.
      setMultiSimResults([]);
      setMultiSimExpanded({});

      if (pairs.length === 1) {
        const inputs = await fetchSimulateInputs(pairs[0].salesOrder, pairs[0].itemNo, {
          silent: true
        });
        if (!inputs) return; // fetchSimulateInputs already showed the error toast
        if (!inputs.productCode) {
          toast.error(`No product code found for Item No ${pairs[0].itemNo}`);
          return;
        }
        const reqDate = inputs.requirementDateStr || requirementDate;
        if (!reqDate) {
          toast.warning("Please select requirement date");
          return;
        }
        const qty =
          Number(inputs.noOfDrumCoil) * Number(inputs.lengthOfDrumCoil) ||
          Number(inputs.qty) ||
          1;

        const { rows, scheduleTree, headingMessage } = await runSimulationFor(
          inputs.productCode,
          qty,
          reqDate
        );
        setHeadingMessage(headingMessage);
        setScheduleTree(scheduleTree);
        setSimulatedData(rows);
        toast.success("Simulation completed");
        return;
      }

      // ── MANUAL MODE (no Sales Order / document selected) ─────────────────

      if (!selectedProductCode) {
        toast.warning("Please select Finished Product");
        return;
      }

      if (!requirementDate) {
        toast.warning("Please select requirement date");
        return;
      }

      const quantity = calculatedQty || 1;
      const { rows, scheduleTree, headingMessage } = await runSimulationFor(
        selectedProductCode.value,
        quantity,
        requirementDate
      );

      setHeadingMessage(headingMessage);
      setScheduleTree(scheduleTree);
      setSimulatedData(rows);
      toast.success("Simulation completed");
    } catch (error) {
      console.error(error);
      toast.error("Simulation failed");
    } finally {
      setIsSimulating(false);
    }
  };

  const renderSchedule = (node, level = 0) => {
    if (!node) return null;

    const isExpanded = expandedNodes[node.productCode] ?? true;

    const toggleNode = () => {
      setExpandedNodes((prev) => ({
        ...prev,
        [node.productCode]: !isExpanded
      }));
    };

    return (
      <div key={node.productCode + level}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            padding: "8px 12px",
            marginLeft: level * 25,
            borderRadius: 6,
            marginBottom: 4,
            background: level === 0 ? "#e3f2fd" : "white",
            border: "1px solid #eee",
            fontWeight: level === 0 ? 700 : 500,
            fontSize: 13,
            cursor: node.children?.length ? "pointer" : "default"
          }}
          onClick={node.children?.length ? toggleNode : undefined}
        >
          {node.children?.length ? (
            <span style={{ marginRight: 8, fontSize: 12 }}>
              {isExpanded ? "▼" : "▶"}
            </span>
          ) : (
            <span style={{ marginRight: 16 }} />
          )}

          <div style={{ flex: 1 }}>{node.productCode}</div>

          <div style={{ fontSize: 12, color: "#555" }}>
            {formatDate(node.startDate)}{" "}
            {new Date(node.startDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
            {" → "}
            {formatDate(node.endDate)}{" "}
            {new Date(node.endDate).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>

        {isExpanded &&
          node.children?.map((child) => renderSchedule(child, level + 1))}
      </div>
    );
  };

  // Rounds to at most 3 decimals (drops float noise like 0.003185000000000003).
  const fmt3 = (v) => {
    if (v === undefined || v === null || v === "" || v === "-") return "-";
    const n = Number(v);
    if (!isFinite(n)) return v;
    return n.toLocaleString(undefined, { maximumFractionDigits: 3 });
  };

  // ── EXCEL EXPORT ──────────────────────────────────────────────────────────
  // The UI keeps one row per BOM occurrence (a product can appear several times
  // with different Qty). The export merges those into ONE row per Product:
  //   Qty, Machine, Labour  -> summed (they scale with quantity)
  //   Setup, all Lead Times -> highest value (they don't add up across occurrences)
  //   Start / End           -> earliest start / latest end
  const EXPORT_COLUMNS = [
    { header: "Product", key: "productCode", width: 18 },
    { header: "Description", key: "description", width: 40 },
    { header: "Product Type", key: "productType", width: 18 },
    { header: "Qty", key: "qty", width: 12 },
    { header: "UOM", key: "uom", width: 8 },
    { header: "Base Unit", key: "baseUnit", width: 10 },
    { header: "Setup", key: "setup", width: 10 },
    { header: "Machine", key: "machine", width: 10 },
    { header: "Labour", key: "labour", width: 10 },
    { header: "Own LT", key: "ownLT", width: 10 },
    { header: "Procurement LT", key: "procLT", width: 15 },
    { header: "Production LT", key: "prodLT", width: 14 },
    { header: "Total LT", key: "totalLT", width: 10 },
    { header: "Start Date", key: "startDate", width: 12 },
    { header: "Start Time", key: "startTime", width: 11 },
    { header: "End Date", key: "endDate", width: 12 },
    { header: "End Time", key: "endTime", width: 11 },
    { header: "Status", key: "status", width: 34 }
  ];

  const round3 = (v) => {
    const n = Number(v);
    return isFinite(n) ? Math.round(n * 1000) / 1000 : 0;
  };

  // Attaches the same Status text the table shows (only the finished-good row has one).
  const rowsWithStatus = (rows, sTree, reqDate) =>
    rows.map((row) => {
      const validation =
        sTree && row.productCode === sTree.productCode && reqDate
          ? validateRequirementDate(row.calculatedLeadTime, reqDate)
          : null;
      return { ...row, status: validation?.message || "" };
    });

  const aggregateRowsForExport = (rows) => {
    const map = new Map();
    rows.forEach((row) => {
      const n = (v) => (isFinite(Number(v)) ? Number(v) : 0);
      const cur = map.get(row.productCode);
      if (!cur) {
        map.set(row.productCode, {
          productCode: row.productCode,
          description: row.description || "",
          productType: row.productType || "",
          qty: n(row.qty),
          uom: row.uom || "",
          baseUnit: row.baseUnit,
          setup: n(row.setupTime),
          machine: n(row.machineTime),
          labour: n(row.labourTime),
          ownLT: n(row.ownTime),
          procLT: n(row.procurementLeadTime),
          prodLT: n(row.productionLeadTime),
          totalLT: n(row.calculatedLeadTime),
          startTs: row.startTs ?? null,
          endTs: row.endTs ?? null,
          status: row.status || ""
        });
        return;
      }
      cur.qty += n(row.qty);
      cur.machine += n(row.machineTime);
      cur.labour += n(row.labourTime);
      cur.setup = Math.max(cur.setup, n(row.setupTime));
      cur.ownLT = Math.max(cur.ownLT, n(row.ownTime));
      cur.procLT = Math.max(cur.procLT, n(row.procurementLeadTime));
      cur.prodLT = Math.max(cur.prodLT, n(row.productionLeadTime));
      cur.totalLT = Math.max(cur.totalLT, n(row.calculatedLeadTime));
      if (row.startTs != null && (cur.startTs == null || row.startTs < cur.startTs)) cur.startTs = row.startTs;
      if (row.endTs != null && (cur.endTs == null || row.endTs > cur.endTs)) cur.endTs = row.endTs;
      if (!cur.status && row.status) cur.status = row.status;
    });

    const timeFmt = { hour: "2-digit", minute: "2-digit" };
    return Array.from(map.values()).map((r) => ({
      productCode: r.productCode,
      description: r.description,
      productType: r.productType,
      qty: round3(r.qty),
      uom: r.uom,
      baseUnit: r.baseUnit,
      setup: round3(r.setup),
      machine: round3(r.machine),
      labour: round3(r.labour),
      ownLT: round3(r.ownLT),
      procLT: r.procLT ? round3(r.procLT) : "",
      prodLT: r.prodLT ? round3(r.prodLT) : "",
      totalLT: round3(r.totalLT),
      startDate: r.startTs != null ? formatDate(new Date(r.startTs)) : "",
      startTime: r.startTs != null ? new Date(r.startTs).toLocaleTimeString([], timeFmt) : "",
      endDate: r.endTs != null ? formatDate(new Date(r.endTs)) : "",
      endTime: r.endTs != null ? new Date(r.endTs).toLocaleTimeString([], timeFmt) : "",
      status: r.status
    }));
  };

  const buildSheet = (rows) => {
    const aoa = [
      EXPORT_COLUMNS.map((c) => c.header),
      ...rows.map((r) => EXPORT_COLUMNS.map((c) => r[c.key] ?? ""))
    ];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    ws["!cols"] = EXPORT_COLUMNS.map((c) => ({ wch: c.width }));
    return ws;
  };

  const safeSheetName = (name, used) => {
    let base = String(name).replace(/[\\/?*[\]:]/g, "-").slice(0, 31) || "Sheet";
    let candidate = base;
    let i = 2;
    while (used.has(candidate.toLowerCase())) {
      const suffix = ` (${i++})`;
      candidate = base.slice(0, 31 - suffix.length) + suffix;
    }
    used.add(candidate.toLowerCase());
    return candidate;
  };

  const hasResults = multiSimResults.length > 0 || simulatedData.length > 0;

  const handleExportExcel = () => {
    if (!hasResults) {
      toast.warning("Run a simulation first, then export");
      return;
    }
    try {
      const wb = XLSX.utils.book_new();
      const used = new Set();

      if (multiSimResults.length > 0) {
        const perGroup = multiSimResults.map((g) => ({
          g,
          rows: rowsWithStatus(g.rows, g.scheduleTree, g.requirementDate)
        }));
        // Combined sheet: every finished good merged into one row per product.
        XLSX.utils.book_append_sheet(
          wb,
          buildSheet(aggregateRowsForExport(perGroup.flatMap((p) => p.rows))),
          safeSheetName("All Finished Goods", used)
        );
        // One sheet per finished good (Sales Order line).
        perGroup.forEach(({ g, rows }, i) => {
          XLSX.utils.book_append_sheet(
            wb,
            buildSheet(aggregateRowsForExport(rows)),
            safeSheetName(`${i + 1}. ${g.productCode} (Item ${g.itemNo})`, used)
          );
        });
      } else {
        XLSX.utils.book_append_sheet(
          wb,
          buildSheet(aggregateRowsForExport(rowsWithStatus(simulatedData, scheduleTree, requirementDate))),
          safeSheetName("Lead Time", used)
        );
      }

      const today = formatDate(new Date()).replace(/\//g, "-");
      XLSX.writeFile(wb, `LeadTime_Simulation_${today}.xlsx`);
      toast.success("Excel exported");
    } catch (err) {
      console.error("Excel export failed:", err);
      toast.error("Excel export failed");
    }
  };

  // Shared table renderer for both the single-product result table and each
  // accordion group's table in multi-finished-good mode.
  const renderResultsTable = (rows, sTree, reqDateForValidation) => (
    <div style={styles.tableContainer}>
      <table style={styles.erpTable}>
        <thead>
          <tr>
            <th style={styles.erpTh}>Product</th>
            <th style={styles.erpTh}>Description</th>
            <th style={styles.erpTh}>Product Type</th>
            <th style={styles.erpTh}>Qty</th>
            <th style={styles.erpTh}>UOM</th>
            <th style={styles.erpTh}>Base Unit</th>
            <th style={styles.erpTh}>Setup</th>
            <th style={styles.erpTh}>Machine</th>
            <th style={styles.erpTh}>Labour</th>
            <th style={styles.erpTh}>Own LT</th>
            <th style={styles.erpTh}>Procurement LT</th>
            <th style={styles.erpTh}>Production LT</th>
            <th style={styles.erpTh}>Total LT</th>
            <th style={styles.erpTh}>Start Date</th>
            <th style={styles.erpTh}>Start Time</th>
            <th style={styles.erpTh}>End Date</th>
            <th style={styles.erpTh}>End Time</th>
            <th style={styles.erpTh}>Status</th>
          </tr>
        </thead>

        <tbody>
          {rows.map((row, i) => {
            const validation =
              sTree && row.productCode === sTree.productCode && reqDateForValidation
                ? validateRequirementDate(row.calculatedLeadTime, reqDateForValidation)
                : null;

            return (
              <tr
                key={i}
                style={{
                  background: row.productCode === sTree?.productCode ? "#fafafa" : "white"
                }}
              >
                <td style={styles.erpTd}>{row.productCode}</td>
                <td style={styles.erpTd}>{row.description || "-"}</td>

                <td style={styles.erpTd}>{row.productType || "-"}</td>

                <td style={{ ...styles.erpTd, fontWeight: 600 }}>{fmt3(row.qty)}</td>
                <td style={styles.erpTd}>{row.uom || "-"}</td>

                <td style={styles.erpTd}>{row.baseUnit}</td>
                <td style={styles.erpTd}>{fmt3(row.setupTime)}</td>
                <td style={styles.erpTd}>{fmt3(row.machineTime)}</td>
                <td style={styles.erpTd}>{fmt3(row.labourTime)}</td>
                <td style={styles.erpTd}>{fmt3(row.ownTime)}</td>
                <td style={styles.erpTd}>{row.procurementLeadTime ? fmt3(row.procurementLeadTime) : "-"}</td>
                <td style={styles.erpTd}>{row.productionLeadTime ? fmt3(row.productionLeadTime) : "-"}</td>

                <td style={{ ...styles.erpTd, fontWeight: 700, color: "#1976d2" }}>
                  {fmt3(row.calculatedLeadTime)}
                </td>

                <td style={styles.erpTd}>{row.startDate}</td>
                <td style={styles.erpTd}>{row.startTime}</td>
                <td style={styles.erpTd}>{row.endDate}</td>
                <td style={styles.erpTd}>{row.endTime}</td>

                <td
                  style={{
                    ...styles.erpTd,
                    fontWeight: 600,
                    color:
                      validation?.status === "valid"
                        ? "green"
                        : validation?.status === "invalid"
                        ? "red"
                        : "#555"
                  }}
                >
                  {validation?.message || "-"}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  // One collapsible row per finished good in multi-finished-good mode —
  // header shows Sales Order / Item No / Product Code / Qty / Requirement
  // Date, body (only rendered while expanded) reuses renderResultsTable.
  const renderMultiSimAccordion = () => (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {multiSimResults.map((g) => {
        const isOpen = !!multiSimExpanded[g.key];
        return (
          <div
            key={g.key}
            style={{ border: "1px solid #e5e7eb", borderRadius: 8, overflow: "hidden" }}
          >
            <div
              onClick={() =>
                setMultiSimExpanded((prev) => ({ ...prev, [g.key]: !prev[g.key] }))
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: 14,
                padding: "10px 14px",
                background: "#f8fafc",
                cursor: "pointer",
                fontSize: 13
              }}
            >
              <span style={{ fontSize: 12 }}>{isOpen ? "▼" : "▶"}</span>
              <span style={{ fontWeight: 700, color: "#1e293b" }}>{g.productCode}</span>
              <span style={{ color: "#64748b" }}>SO: {g.salesOrder}</span>
              <span style={{ color: "#64748b" }}>Item No: {g.itemNo}</span>
              <span style={{ color: "#64748b" }}>Qty: {g.qty}</span>
              <span style={{ color: "#64748b" }}>Req. Date: {formatDate(g.requirementDate)}</span>
              <button
                type="button"
                disabled={!g.scheduleTree}
                onClick={(e) => {
                  e.stopPropagation(); // don't toggle the accordion
                  setExpandedNodes({});
                  setModalGroup(g);
                  setShowModal(true);
                }}
                style={{
                  ...(g.scheduleTree ? styles.secondaryBtn : styles.secondaryBtnDisabled),
                  marginLeft: "auto",
                  height: 30,
                  padding: "0 14px"
                }}
              >
                View BOM Schedule
              </button>
            </div>

            {isOpen && (
              <div style={{ padding: 10 }}>
                {g.headingMessage && (
                  <div style={{ ...styles.banner, marginBottom: 10 }}>{g.headingMessage}</div>
                )}
                {renderResultsTable(g.rows, g.scheduleTree, g.requirementDate)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <Layout>
      <div style={{ padding: 20 }}>
        {/* HEADER */}
        <div style={styles.pageTitleBar}>
          <div>
            <h2 style={styles.pageTitle}>Lead Time Management</h2>
            <p style={styles.pageSubtitle}>
              Simulate production &amp; procurement lead times
            </p>
          </div>
          <div style={{ fontSize: 12, fontWeight: 600, color: "#64748b" }}>
            UOM: Days
          </div>
        </div>

        {/* SPEC FILTERS (Product Type / Category / Cable Type / ...) — same as Pegging */}
        <div style={styles.filterCard}>
          <button
            type="button"
            onClick={() => setFiltersOpen((o) => !o)}
            aria-expanded={filtersOpen}
            aria-controls="lead-filters-body"
            style={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "0 0 10px 0",
              background: "transparent",
              border: "none",
              borderBottom: filtersOpen ? "1px solid #eef0f3" : "none",
              cursor: "pointer",
              textAlign: "left"
            }}
          >
            <span style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {activeFilterCount > 0 && (
                <span
                  style={{
                    background: "#0d6efd",
                    color: "#fff",
                    borderRadius: 10,
                    padding: "1px 8px",
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  {activeFilterCount} active
                </span>
              )}
            </span>
            <span style={{ display: "flex", alignItems: "center", gap: 14 }}>
              {activeFilterCount > 0 && (
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    clearAllFilters();
                  }}
                  style={{
                    padding: "4px 10px",
                    background: "#fff",
                    color: "#c0392b",
                    border: "1px solid #c0392b",
                    borderRadius: 4,
                    fontSize: 12,
                    fontWeight: 600
                  }}
                >
                  Clear Filters
                </span>
              )}
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 6,
                  padding: "5px 12px",
                  background: "#fff",
                  color: "#334155",
                  border: "1px solid #dee2e6",
                  borderRadius: 6,
                  fontSize: 12,
                  fontWeight: 600
                }}
              >
                {filtersOpen ? "Hide Filters" : "Show Filters"}
                <span
                  style={{
                    display: "inline-block",
                    fontSize: 12,
                    color: "#555",
                    transition: "transform 0.25s ease",
                    transform: filtersOpen ? "rotate(180deg)" : "rotate(0deg)"
                  }}
                >
                  ▼
                </span>
              </span>
            </span>
          </button>

          <div
            id="lead-filters-body"
            style={{
              display: "grid",
              gridTemplateRows: filtersOpen ? "1fr" : "0fr",
              transition: "grid-template-rows 0.3s ease"
            }}
          >
            <div style={{ overflow: filtersOpen ? "visible" : "hidden", minHeight: 0 }}>
              <div style={{ paddingTop: 14 }}>
                <div style={{ minWidth: 200, maxWidth: 260, marginBottom: 14 }}>
                  {FILTER_FIELDS.filter((f) => f.key === "productType").map((f) => (
                    <div key={f.key}>
                      <label style={styles.compactLabel}>{f.label}</label>
                      <Select
                        options={(dropdownData[f.dataKey] || []).map((item) => ({
                          value: item.name,
                          label: item.name
                        }))}
                        value={
                          filterValues[f.key]
                            ? { value: filterValues[f.key], label: filterValues[f.key] }
                            : null
                        }
                        onChange={(selected) => handleFilterChange(f.key, selected)}
                        placeholder="Select"
                        isClearable
                        styles={customSelectStyles}
                        menuPortalTarget={document.body}
                        menuPosition="fixed"
                      />
                    </div>
                  ))}
                </div>

                <div style={compactFilterBox}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                    {(isRawMaterialType(filterValues.productType) ? RAW_MATERIAL_FILTER_KEYS : CABLE_SPEC_FILTER_KEYS)
                      .map((key) => FILTER_FIELDS.find((f) => f.key === key))
                      .map((f) => (
                        <div
                          key={f.key}
                          style={{
                            minWidth: 120,
                            flex: "1 1 110px",
                            maxWidth: 180,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3
                          }}
                        >
                          <span style={compactFilterItemLabel}>{f.label}</span>
                          <Select
                            options={(dropdownData[f.dataKey] || []).map((item) => ({
                              value: item.name,
                              label: item.name
                            }))}
                            value={
                              filterValues[f.key]
                                ? { value: filterValues[f.key], label: filterValues[f.key] }
                                : null
                            }
                            onChange={(selected) => handleFilterChange(f.key, selected)}
                            placeholder="Select"
                            isClearable
                            styles={compactSelectStylesSmall}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>
                      ))}
                  </div>
                </div>

                <div style={compactFilterBox}>
                  <div style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                    {TOP_CATEGORY_FILTERS.map((cat) => (
                      <React.Fragment key={cat}>
                        <div
                          style={{
                            minWidth: 140,
                            flex: "1 1 140px",
                            maxWidth: 200,
                            display: "flex",
                            flexDirection: "column",
                            gap: 3
                          }}
                        >
                          <span style={compactFilterItemLabel}>{cat}</span>
                          <Select
                            options={(cat === "Sales Order" ? allSalesOrders : categoryOptions[cat] || []).map((opt) => ({
                              value: opt,
                              label: opt
                            }))}
                            value={
                              categoryElementFilters[cat]
                                ? { value: categoryElementFilters[cat], label: categoryElementFilters[cat] }
                                : null
                            }
                            onChange={(selected) =>
                              handleCategoryElementFilterChange(cat, selected?.value || "")
                            }
                            placeholder="All"
                            isClearable
                            styles={compactSelectStylesSmall}
                            menuPortalTarget={document.body}
                            menuPosition="fixed"
                          />
                        </div>

                        {cat === "Sales Order" && (
                          <div
                            style={{
                              minWidth: 110,
                              flex: "1 1 110px",
                              maxWidth: 150,
                              display: "flex",
                              flexDirection: "column",
                              gap: 3
                            }}
                          >
                            <span style={compactFilterItemLabel}>Item No</span>
                            <Select
                              options={salesOrderItemNoOptions.map((opt) => ({
                                value: opt,
                                label: opt
                              }))}
                              value={
                                soItemNoFilter ? { value: soItemNoFilter, label: soItemNoFilter } : null
                              }
                              onChange={(selected) => setSoItemNoFilter(selected?.value || "")}
                              placeholder={
                                categoryElementFilters["Sales Order"] ? "All" : "Select SO first"
                              }
                              isClearable
                              isDisabled={!categoryElementFilters["Sales Order"]}
                              styles={compactSelectStylesSmall}
                              menuPortalTarget={document.body}
                              menuPosition="fixed"
                            />
                          </div>
                        )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FILTER / INPUT CARD */}
        <div style={styles.filterCard}>
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: 12,
              flexWrap: "wrap"
            }}
          >
            <div style={styles.fieldBox}>
              <label style={styles.compactLabel}>Product Code</label>
              <Select
                styles={customSelectStyles}
                options={productCodeOptions}
                value={selectedProductCode}
                onChange={(v) => setSelectedProductCode(v)}
                placeholder="Select Product Code"
                isClearable
                menuPortalTarget={document.body}
                menuPosition="fixed"
                menuPlacement="auto"
                maxMenuHeight={250}
              />
            </div>

            <div style={{ minWidth: 100, flex: "0 0 100px" }}>
              <label style={styles.compactLabel}>No. of Drum/Coil</label>
              <input
                type="number"
                value={factor}
                onChange={(e) => setFactor(e.target.value)}
                style={styles.compactInput}
              />
            </div>

            <div style={{ minWidth: 100, flex: "0 0 100px" }}>
              <label style={styles.compactLabel}>Length of Drum/Coil</label>
              <input
                type="number"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                style={styles.compactInput}
              />
            </div>

            <div style={{ minWidth: 170, flex: "0 0 170px" }}>
              <label style={styles.compactLabel}>Requirement Date</label>
              <div style={{ position: "relative" }}>
                {/* Visible field: always shows dd/mm/yyyy, independent of the
                    browser/OS locale (a native <input type="date"> renders
                    using the system's own format, which is why yyyy-mm-dd
                    was showing regardless of what we set). This is read-only
                    text — the actual date picking happens on the hidden
                    native date input below via showPicker(). */}
                <input
                  type="text"
                  readOnly
                  value={requirementDate ? formatDate(requirementDate) : ""}
                  placeholder="dd/mm/yyyy"
                  onClick={() => {
                    if (requirementDateRef.current?.showPicker) {
                      requirementDateRef.current.showPicker();
                    } else {
                      requirementDateRef.current?.focus();
                    }
                  }}
                  style={{ ...styles.compactInput, paddingRight: 32, cursor: "pointer" }}
                />
                {/* Hidden native date input — supplies the actual picker UI
                    and keeps requirementDate in yyyy-mm-dd for the API. */}
                <input
                  ref={requirementDateRef}
                  type="date"
                  value={requirementDate}
                  onChange={(e) => setRequirementDate(e.target.value)}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    height: "100%",
                    opacity: 0,
                    pointerEvents: "none",
                    border: "none"
                  }}
                  tabIndex={-1}
                />
                <span
                  onClick={() => {
                    if (requirementDateRef.current?.showPicker) {
                      requirementDateRef.current.showPicker();
                    } else {
                      requirementDateRef.current?.focus();
                    }
                  }}
                  style={{
                    position: "absolute",
                    right: 8,
                    top: "50%",
                    transform: "translateY(-50%)",
                    cursor: "pointer",
                    fontSize: 15,
                    color: "#64748b",
                    lineHeight: 1,
                    userSelect: "none"
                  }}
                  title="Select date"
                >
                  📅
                </span>
              </div>
            </div>

            <div style={{ minWidth: 100, flex: "0 0 100px" }}>
              <label style={styles.compactLabel}>Quantity</label>
              <input
                type="number"
                value={calculatedQty}
                disabled
                style={styles.compactInputDisabled}
              />
            </div>

            <div style={{ display: "flex", gap: 10 }}>
              <button style={styles.primaryBtn} onClick={handleSimulate} disabled={isSimulating}>
                {isSimulating ? "Simulating..." : "Simulate"}
              </button>

              <button
                style={
                  scheduleTree ? styles.secondaryBtn : styles.secondaryBtnDisabled
                }
                disabled={!scheduleTree}
                onClick={() => {
                  setExpandedNodes({});
                  setModalGroup(null);
                  setShowModal(true);
                }}
              >
                View BOM Schedule
              </button>

              <button
                style={hasResults ? styles.secondaryBtn : styles.secondaryBtnDisabled}
                disabled={!hasResults}
                onClick={handleExportExcel}
              >
                Export to Excel
              </button>
            </div>
          </div>
        </div>

        {/* RESULTS: single-product table, OR multi-finished-good accordion */}
        {multiSimResults.length > 0 ? (
          renderMultiSimAccordion()
        ) : (
          <>
            {headingMessage && <div style={styles.banner}>{headingMessage}</div>}
            {simulatedData.length > 0 &&
              renderResultsTable(simulatedData, scheduleTree, requirementDate)}
          </>
        )}

        {/* BOM SCHEDULE MODAL */}
        {showModal && activeScheduleTree && (
          <div style={styles.modalBackdrop} onClick={() => { setShowModal(false); setModalGroup(null); }}>
            <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
              <div style={styles.modalHeader}>
                <h3 style={{ margin: 0, fontSize: 16, color: "#1e293b" }}>
                  BOM Production Schedule
                  {modalGroup
                    ? ` — ${modalGroup.productCode} (SO: ${modalGroup.salesOrder}, Item No: ${modalGroup.itemNo})`
                    : ""}
                </h3>
                <button
                  onClick={() => { setShowModal(false); setModalGroup(null); }}
                  style={{
                    background: "#d32f2f",
                    color: "white",
                    border: "none",
                    padding: "6px 14px",
                    borderRadius: 6,
                    cursor: "pointer",
                    fontSize: 13,
                    fontWeight: 600
                  }}
                >
                  Close
                </button>
              </div>

              <div style={styles.modalBody}>{renderSchedule(activeScheduleTree)}</div>
            </div>
          </div>
        )}
      </div>

      <ToastContainer position="top-right" autoClose={3000} />
    </Layout>
  );
};

export default Lead;